import { Controller } from "@hotwired/stimulus"
import { FOCUS_SCROLL_GRACE_MS, computeTipPosition, decideOnViewportChange, isAnchorVisible } from "../lib/metric_tip_position"

// 指標ラベルの「意味と計算式」の吹き出し(metric_label ヘルパーが描画する span に付く)。
//
// - マウスを載せる / キーボードでフォーカスすると表示し、離れると閉じる。Esc でも閉じる
// - ⓘ のクリック・タップで開閉する(開いたまま固定)。クリックは stopPropagation して、
//   table_sort.js の th のクリック(ソート)を起こさない。ラベル文字のクリックは従来どおり th に伝わる
// - 吹き出しはページに1つだけ body 直下に作って使い回す(overflow-x-auto の中でも切れないよう position: fixed)
// - スクロール・リサイズ中はアンカーに付いて位置を計算し直し、アンカーが見えなくなったら閉じる。
//   例外はキーボードでフォーカスした直後(FOCUS_SCROLL_GRACE_MS の間、アンカーが一度見えるまで)だけ。
//   Tab で画面外のラベルにフォーカスしたときのブラウザのスクロールは scroll-behavior: smooth で数フレームかけて動くため、
//   途中のフレームではアンカーがまだ画面外にある。クリックでフォーカスが乗っただけ・猶予が過ぎたあとのユーザーのスクロールでは閉じる
// - 外側のタップで閉じ、turbo:before-cache で取り除く
// - 説明文は textContent で描画する(innerHTML は使わない)

const BUBBLE_ID = "metric-tip-bubble"
const BUBBLE_CLASS = "bg-neutral text-neutral-content text-xs leading-relaxed rounded px-2 py-1 shadow"

let bubble = null
let owner = null

function ensureBubble() {
  if (bubble && bubble.isConnected) return bubble

  bubble = document.createElement("div")
  bubble.id = BUBBLE_ID
  bubble.setAttribute("role", "tooltip")
  bubble.className = BUBBLE_CLASS
  Object.assign(bubble.style, {
    position: "fixed",
    top: "0px",
    left: "0px",
    zIndex: "1000",
    maxWidth: "min(20rem, calc(100vw - 16px))",
    whiteSpace: "pre-line",
    pointerEvents: "none",
    display: "none",
  })
  document.body.appendChild(bubble)
  return bubble
}

function removeBubble() {
  if (owner) owner.close()
  bubble?.remove()
  bubble = null
}

document.addEventListener("turbo:before-cache", removeBubble)

export default class extends Controller {
  static values = { text: String }

  connect() {
    this.pinned = false
    this.onOutsidePointerDown = this.onOutsidePointerDown.bind(this)
    this.onViewportChange = this.onViewportChange.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.frame = null
    this.scrollContainer = null
    this.focusScrollUntil = 0
  }

  disconnect() {
    if (owner === this) this.close()
  }

  // マウスだけホバーで開閉する(タッチのタップで出る擬似 hover は ⓘ の toggle に任せる)
  enter(event) {
    if (event.pointerType && event.pointerType !== "mouse") return
    this.open()
  }

  leave(event) {
    if (event.pointerType && event.pointerType !== "mouse") return
    if (!this.pinned) this.close()
  }

  // キーボードでのフォーカスだけで開く(クリック・タップでのフォーカスでは開かない)
  // Tab で画面外のラベルへ移ったときは、このあとブラウザが smooth スクロールで見える位置まで動かすので、その間は閉じない
  focus() {
    if (!this.element.matches(":focus-visible")) return

    this.open()
    if (this.isOpen) this.focusScrollUntil = performance.now() + FOCUS_SCROLL_GRACE_MS
  }

  blur() {
    if (!this.pinned) this.close()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isOpen && this.pinned) {
      this.close()
    } else {
      this.open()
      this.pinned = true
    }
  }

  get isOpen() {
    return owner === this && bubble?.style.display !== "none"
  }

  open() {
    const text = this.textValue
    if (!text) return

    if (owner && owner !== this) owner.close()

    const el = ensureBubble()
    el.textContent = text
    el.style.display = "block"
    this.position()

    this.element.setAttribute("aria-describedby", BUBBLE_ID)

    if (owner !== this) {
      owner = this
      document.addEventListener("pointerdown", this.onOutsidePointerDown, true)
      document.addEventListener("keydown", this.onKeydown)
      window.addEventListener("scroll", this.onViewportChange, { capture: true, passive: true })
      window.addEventListener("resize", this.onViewportChange, { passive: true })
    }
  }

  // 吹き出しの大きさを測って位置を決める。
  // position: fixed の幅は left からビューポート右端までに縮むので、前回の left/top が残ったまま測ると
  // 右端寄りで開いたあとに狭く測られてしまう。測る前に左上へ戻す(visibility: hidden の間に行う)
  position() {
    const el = bubble
    if (!el) return

    el.style.visibility = "hidden"
    el.style.left = "0px"
    el.style.top = "0px"

    const rect = this.element.getBoundingClientRect()
    const { top, left, placement } = computeTipPosition({
      anchor: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width },
      tip: { width: el.offsetWidth, height: el.offsetHeight },
      viewport: { width: document.documentElement.clientWidth, height: window.innerHeight },
    })
    el.style.top = `${top}px`
    el.style.left = `${left}px`
    el.dataset.placement = placement
    el.style.visibility = "visible"
  }

  close() {
    this.pinned = false
    this.focusScrollUntil = 0
    this.element.removeAttribute("aria-describedby")
    if (owner !== this) return

    owner = null
    if (bubble) bubble.style.display = "none"
    document.removeEventListener("pointerdown", this.onOutsidePointerDown, true)
    document.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("scroll", this.onViewportChange, { capture: true })
    window.removeEventListener("resize", this.onViewportChange)
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
    this.scrollContainer = null
  }

  onOutsidePointerDown(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  // スクロール・リサイズはフレームごとにまとめて、アンカーが見えている(またはキーボードフォーカス直後の猶予中)なら
  // 位置を計算し直し、見えなくなったら閉じる。表の横スクロールなど、アンカーを含む要素のスクロールではその要素の枠も見える範囲に含める
  onViewportChange(event) {
    const target = event?.target
    if (target instanceof Element && target.contains(this.element)) {
      this.scrollContainer = target
    } else if (event?.type === "scroll") {
      this.scrollContainer = null
    }
    if (this.frame) return

    this.frame = requestAnimationFrame(() => {
      this.frame = null
      if (!this.isOpen) return

      const rect = this.element.getBoundingClientRect()
      const clip = this.scrollContainer?.isConnected ? this.scrollContainer.getBoundingClientRect() : null
      const viewport = { width: document.documentElement.clientWidth, height: window.innerHeight }
      const { keepOpen, focusScrollUntil } = decideOnViewportChange({
        anchorVisible: isAnchorVisible({ anchor: rect, viewport, clip }),
        keyboardFocused: this.element.matches(":focus-visible"),
        now: performance.now(),
        focusScrollUntil: this.focusScrollUntil,
      })
      this.focusScrollUntil = focusScrollUntil
      if (keepOpen) {
        this.position()
      } else {
        this.close()
      }
    })
  }
}
